class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    // Warm the reconcile on intent, not on click. Hovering or tabbing to the cart icon is
    // the earliest honest signal that the drawer is about to be opened, and it buys the
    // round trip a head start of roughly a hover's worth — so the corrected cart is
    // usually already in hand by the time the panel finishes sliding in. `once` because
    // one warm fetch is the whole point; the open path still reconciles on its own.
    const warm = () => this.reconcileWithCart();
    cartLink.addEventListener('pointerenter', warm, { once: true });
    cartLink.addEventListener('focus', warm, { once: true });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();

    this.reconcileWithCart();
    this.bindScrollEdges();
  }

  // Scroll edges. The list scrolls flush against the header rule, so a scrolled row was
  // sliced mid-glyph by a hairline with nothing to say it had been scrolled — it read as a
  // rendering fault rather than as more content. Two state classes: one when the list is
  // scrolled away from the top (the header earns a shadow), one while there is still
  // something below (the bottom edge fades).
  //
  // Bound in the CAPTURE phase on <cart-drawer> itself, which is the only node here that
  // survives every re-render: scroll does not bubble, and cart-drawer-items is replaced
  // wholesale on the pub/sub path, so a listener on the scroller would be lost on the first
  // quantity change.
  bindScrollEdges() {
    if (!this.scrollEdgesBound) {
      this.addEventListener('scroll', (event) => this.updateScrollEdges(event.target), true);
      this.scrollEdgesBound = true;
    }
    requestAnimationFrame(() => this.updateScrollEdges(this.querySelector('cart-drawer-items')));
  }

  updateScrollEdges(scroller) {
    if (!scroller || scroller.tagName !== 'CART-DRAWER-ITEMS') return;
    const remaining = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
    this.classList.toggle('is-scrolled', scroller.scrollTop > 2);
    this.classList.toggle('has-more-below', remaining > 2);
  }

  // The drawer's markup is rendered once, with the page. Anything that changes the cart
  // WITHOUT going through this theme's own JS leaves it stale: a back/forward restore from
  // the bfcache, a second tab, a third-party app posting to /cart, or a bare fetch to
  // /cart/add. The shopper then opens the drawer and is shown a cart that is not theirs —
  // in the worst case the empty state over a basket with items in it, which is also why
  // Dawn's `.is-empty + .drawer__footer { display: none }` could hide the checkout button
  // on a non-empty cart.
  //
  // So: on every open, ask what the cart actually is. Re-render only when the answer
  // disagrees with what is on screen, so the normal path costs one cached request and no
  // DOM work. Deliberately fire-and-forget — the drawer opens immediately either way and
  // patches itself a moment later rather than making the shopper wait on a round trip.
  reconcileWithCart() {
    if (this.reconciling) return;
    this.reconciling = true;

    // ONE request, not two. Asking /cart.js first and only then fetching the section was
    // correct but serial, and it put the self-correction ~1.6s after the open. Fetching the
    // section straight away and diffing its line count against what is on screen halves
    // that, and the response is the thing we need anyway when it does differ.
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        const source = fresh.querySelector('#CartDrawer');
        const target = this.querySelector('#CartDrawer');
        if (!source || !target) return;

        const freshLines = source.querySelectorAll('.cart-item').length;
        const renderedLines = target.querySelectorAll('.cart-item').length;
        // Line COUNT, not item count: quantity changes are cart.js's job and re-rendering
        // on those would fight it mid-interaction.
        if (freshLines === renderedLines) return;

        target.innerHTML = source.innerHTML;
        this.classList.toggle('is-empty', freshLines === 0);
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
      })
      .catch(() => {})
      .finally(() => {
        this.reconciling = false;
      });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
